import * as React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { createSelector } from 'reselect'
import { EngagementSection, Section } from '../../clientModels'
import { isDefined } from '../../guards'
import { getEngagementSections, getSections } from '../../reducers/selectors'
import {
  ButtonConfiguration,
  clientUserButtonConfig,
  rsmUserButtonConfig,
} from '../../services/engagementPhaseService'
import { AppState } from '../../store'
import { EngagementPhaseChangeProps } from '../engagementPhaseChange'
import Icon from '../icon/icon'
import './questionListButtons.scss'
import { useEffect, useRef, useState } from 'react'
import * as confirmModalActions from '../../actions/confirmModalActions'
import { getEngagement } from '../../actions/engagementThunks'
import { httpBeginAction, httpEndAction } from '../../actions/httpActions'
import { getIsRunningRules } from '../../actions/rulesThunks'
import Footer from '../footer/footer'

export const VERIFY_SUBMIT_READY_ID = 'verify-submit-ready'

interface PreviousNextSection {
  previousSectionId?: number
  nextSectionId?: number
}

// tslint:disable-next-line:no-empty-interface - this is just renaming the EngagementPhaseChangeProps
interface QuestionListButtonsOwnProps extends EngagementPhaseChangeProps {}

// tslint:disable-next-line:no-empty-interface - so far this needs no additional properties
interface QuestionListButtonsMappedProps extends PreviousNextSection {}

function getSortedSections() {
  return createSelector(
    (state: AppState, props: QuestionListButtonsOwnProps) => getSections(state),
    (state: AppState, props: QuestionListButtonsOwnProps) =>
      getEngagementSections(state, props.engagement.id) || {},
    (state: AppState, props: QuestionListButtonsOwnProps) =>
      props.engagement.id,
    (sections, engagementSections, engagementId) => {
      const sortedSections = Object.values(sections)
        .filter(isDefined)
        .sort(
          (left: Section, right: Section) =>
            left.displayOrder - right.displayOrder
        )
      const sortedEngagementSections: EngagementSection[] = []
      for (const section of sortedSections) {
        const engagementSection = engagementSections[section.id]

        if (engagementSection) {
          if (!section.isVisible) {
            engagementSection.isVisible = section.isVisible
          }
          sortedEngagementSections.push(engagementSection)
        } else {
          sortedEngagementSections.push({
            id: section.id,
            engagementId,
            isVisible: section.isVisible,
            isVisibleDebug: 0,
          })
        }
      }
      return sortedEngagementSections
    }
  )
}

function getPreviousNextSection() {
  const getSectionsSorted = getSortedSections()
  return createSelector(
    getSectionsSorted,
    (state: AppState, props: QuestionListButtonsOwnProps) => props.sectionId,
    (sections, currentSectionId) => {
      const result: PreviousNextSection = {}

      if (!currentSectionId) {
        return result
      }

      let next = false

      for (const section of sections) {
        if (section.id === currentSectionId) {
          next = true
          continue
        }
        if (!next) {
          if (section.isVisible || section.isVisibleDebug === 1) {
            result.previousSectionId = section.id
          }
        } else {
          if (section.isVisible || section.isVisibleDebug === 1) {
            result.nextSectionId = section.id
            break
          }
        }
      }

      return result
    }
  )
}

type QuestionListButtonsProps = QuestionListButtonsOwnProps &
  QuestionListButtonsMappedProps

const QuestionListButtons = (props: QuestionListButtonsProps) => {
  const { user, engagement, navigateSection, engagementTemplate } = props
  const [config, setConfig] = useState<ButtonConfiguration | undefined>()
  const [modalDisplayed, setModalDisplayed] = useState(false)
  const busy = useSelector((appState: AppState) =>
    appState.http.loading.includes('update-engagement-phase')
  )
  // True while any answer-save round-trip is in flight. The save handler
  // (answerActionHelpers.performMetadataUpdate) dispatches the server-side
  // rules + visibility recompute BEFORE httpSaveEndAction fires, so by the
  // time isSaving flips to false the engagement counts already reflect the
  // final post-rules state. Gating the auto-open on !isSaving collapses the
  // QA delay to roughly the save-roundtrip duration -- no need to wait out
  // a long fixed debounce that punishes UAT/PROD where the save is fast.
  const isSaving = useSelector(
    (appState: AppState) => appState.http.saving.length > 0
  )
  // Set while a verify-refetch (see useEffect below) is in flight. Used
  // to suppress the SUBMIT button so it doesn't flash when the refetch
  // is about to reveal newly-visible questions.
  const isVerifyingSubmitReady = useSelector((appState: AppState) =>
    appState.http.loading.includes(VERIFY_SUBMIT_READY_ID)
  )
  // True whenever any answer on this engagement has been locally modified
  // but not yet server-confirmed. answerPartialChanged sets isDirty=true on
  // every keystroke; the flag clears only after answerSaveAction.success
  // lands. Between the user typing in the LAST currently-visible question
  // and the eventual blur/save, isSaving is still false -- so without this
  // gate the count equality (54/54) leaks through and the modal/ribbon
  // briefly fire while the user is still editing.
  const hasDirtyAnswers = useSelector((appState: AppState) => {
    const answers = appState.engagementQuestions[engagement.id] || {}
    return Object.values(answers).some(a => !!a && a.isDirty)
  })
  const getPreviousNextSectionSelector = getPreviousNextSection()
  const previousNextSection: PreviousNextSection = useSelector(
    (appState: AppState) => getPreviousNextSectionSelector(appState, props)
  )
  const { previousSectionId, nextSectionId } = previousNextSection
  const dispatch = useDispatch()
  // Track the last count-signature that we've re-verified against the
  // server via a fresh getEngagement. External users on templates like
  // 990-EZ can hit a false "12/12 = complete" state because the client-
  // side rules engine only counts already-loaded questions -- sections
  // whose visibility depends on a gating answer (Q1.4, Q1.14, ...) may
  // still be dormant on the client even after the server has expanded
  // them. Refetching before firing the SUBMIT modal reconciles this;
  // the ref remembers which signature we already verified so we don't
  // loop-refetch every render.
  const verifiedSignatureRef = useRef<string | null>(null)
  const verifyingRef = useRef<boolean>(false)

  useEffect(() => {
    if (user) {
      const config = user.isExternal
        ? clientUserButtonConfig(engagement)
        : rsmUserButtonConfig(engagement, user, engagementTemplate)

      // Auto-open the SUBMIT modal once the save round-trip settles.
      //
      // Two transients can flip phaseButtonText into SUBMIT briefly:
      //  (a) the local rules cycle after the user answers the last
      //      currently-visible question, before the answer is sent;
      //  (b) the server response carrying rule-driven visibility changes
      //      that expand totalQuestions.
      //
      // Both transients are covered by gating on !isSaving: the save
      // handler runs runRulesOnNewAnswers (which dispatches visibility
      // updates) BEFORE httpSaveEndAction fires, so the render where
      // isSaving flips to false already shows the final counts. We keep
      // a small fire-time setTimeout to swallow the ~10ms window between
      // the answer-changed render and httpSaveBeginAction -- without it
      // the modal can flash inside that gap before the save begins.
      let submitTimer: number | undefined
      const submitReady =
        engagement.loaded &&
        config?.phaseButtonText === 'SUBMIT' &&
        !modalDisplayed &&
        !busy &&
        !isSaving &&
        !hasDirtyAnswers &&
        !getIsRunningRules()

      if (submitReady) {
        // For external users, verify with a fresh getEngagement before
        // firing. If the server has additional visible questions we
        // haven't loaded yet, this refetch will populate them and the
        // next render will drop phaseButtonText away from SUBMIT --
        // preventing the premature-modal case reported on 990-EZ. If
        // counts match after the refetch, we cache the signature and
        // fall through to the normal setTimeout path.
        const signature = `${engagement.successfulForUserQuestions}-${engagement.totalVisibleQuestions}-${engagement.successfulQuestions}-${engagement.totalQuestions}`
        if (
          user.isExternal &&
          verifiedSignatureRef.current !== signature
        ) {
          if (!verifyingRef.current) {
            verifyingRef.current = true
            dispatch(httpBeginAction({ id: VERIFY_SUBMIT_READY_ID }))
            Promise.resolve(dispatch(getEngagement(engagement.id) as any))
              .catch(() => undefined)
              .then(() => {
                verifiedSignatureRef.current = signature
                verifyingRef.current = false
                dispatch(httpEndAction({ id: VERIFY_SUBMIT_READY_ID }))
              })
          }
        } else {
          submitTimer = window.setTimeout(() => {
            if (!getIsRunningRules()) {
              showModal()
              setModalDisplayed(true)
            }
          }, 300)
        }
      } else if (!config?.phaseButtonText && modalDisplayed) {
        setModalDisplayed(false)
      }

      setConfig(config)
      return () => {
        if (submitTimer !== undefined) {
          window.clearTimeout(submitTimer)
        }
      }
    }
    // eslint-disable-next-line
  }, [user, engagement, isSaving, hasDirtyAnswers])

  const handlePrevButtonClick = () => {
    if (previousSectionId) {
      navigateSection(engagement.id, previousSectionId)
    }
  }

  const handleNextButtonClick = () => {
    if (nextSectionId) {
      navigateSection(engagement.id, nextSectionId)
    }
  }

  const showModal = () => {
    dispatch(
      confirmModalActions.setConfirmModalAction({
        confirmModal: { phaseChange: 'PBC' },
      })
    )
  }

  if (!user || !engagement) {
    return null
  }

  return (
    <div className='question-list-buttons'>
      <div className='btn'>
        <button
          className='btn navigate'
          disabled={!previousSectionId}
          onClick={handlePrevButtonClick}
        >
          <Icon icon='isClosed' className='lesser' />{' '}
          <span className='d-none d-sm-block'> Previous Task</span>
        </button>
        <button
          className='btn navigate'
          disabled={!nextSectionId}
          onClick={handleNextButtonClick}
        >
          <span className='d-none d-sm-block'>Next Task </span>{' '}
          <Icon icon='isClosed' className='greater' />
        </button>

        {config?.phaseButtonText &&
          !busy &&
          !isSaving &&
          !hasDirtyAnswers &&
          !isVerifyingSubmitReady && (
            // busy check prevents phase buttons from popping in and out
            // while engagement is being updated. !isSaving &&
            // !hasDirtyAnswers additionally suppresses the button during
            // the transient count-equality window (e.g. last answer of
            // section 1+2) where phaseButtonText briefly resolves to
            // 'SUBMIT' before visibility expands -- without this gate the
            // SUBMIT button flashes in for a moment and disappears.
            // !isVerifyingSubmitReady suppresses the button during the
            // external-user server-verify refetch (see useEffect above)
            // so it doesn't flash while the refetch reveals newly-visible
            // sections on templates like 990-EZ.
            <button className='btn btn-primary phase' onClick={showModal}>
              {config.phaseButtonText}
            </button>
          )}
      </div>
      <div className='footer2visible'>
        <Footer />
      </div>
    </div>
  )
}

export default QuestionListButtons
